import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const getAvailabilityStatus = (vehicle) => {
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const availability = vehicle?.vehicle_availability || [];
  if (availability?.length === 0) return { label: 'Available Now', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)', dot: '#22c55e' };
  const todayRecord = availability?.find(a => {
    const from = a?.date_from;
    const to = a?.date_to;
    return from <= today && today <= to;
  });
  if (!todayRecord) return { label: 'Available Now', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)', dot: '#22c55e' };
  if (todayRecord?.avail_status === 'booked') return { label: 'Booked', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', dot: '#ef4444' };
  if (todayRecord?.avail_status === 'blocked') return { label: 'Unavailable', color: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)', dot: '#f97316' };
  return { label: 'Available Now', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)', dot: '#22c55e' };
};

const VehicleCard = ({ vehicle, onClick, onBook }) => {
  const pricing = vehicle?.vehicle_pricing?.[0];
  const primaryMedia = vehicle?.vehicle_media?.find(m => m?.is_primary) || vehicle?.vehicle_media?.[0];
  const [imgUrl, setImgUrl] = useState(null);
  const availability = getAvailabilityStatus(vehicle);
  const isAvailable = availability?.label === 'Available Now';

  useEffect(() => {
    if (primaryMedia?.storage_path) {
      const { data } = supabase?.storage?.from('vehicle-media')?.getPublicUrl(primaryMedia?.storage_path);
      if (data?.publicUrl) setImgUrl(data?.publicUrl);
    }
  }, [primaryMedia]);

  const modeLabel = vehicle?.rental_mode === 'both' ? 'Self-Drive & Chauffeur'
    : vehicle?.rental_mode === 'chauffeur' ? 'Chauffeur Only' : 'Self-Drive';

  return (
    <div className="group rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#ffd700]/30 transition-all duration-300 hover:shadow-[0_8px_32px_rgba(255,215,0,0.10)] flex flex-col">
      {/* Image */}
      <div
        className="relative h-48 bg-white/5 overflow-hidden cursor-pointer"
        onClick={onClick}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={`${vehicle?.make} ${vehicle?.model} ${vehicle?.year}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">🚗</span>
          </div>
        )}
        {/* Mode badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.3)' }}>
            {modeLabel}
          </span>
        </div>
        {/* Rating badge */}
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
          <Icon name="Star" size={12} className="text-[#ffd700] fill-[#ffd700]" />
          <span className="text-white text-xs font-bold">4.8</span>
        </div>
        {/* Availability status badge */}
        <div
          className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
          style={{ background: availability?.bg, color: availability?.color, border: `1px solid ${availability?.border}`, backdropFilter: 'blur(8px)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: availability?.dot }} />
          {availability?.label}
        </div>
      </div>
      {/* Card Body */}
      <div className="p-4 flex flex-col flex-1 cursor-pointer" onClick={onClick}>
        <div className="mb-2">
          <h3 className="text-white font-bold text-base leading-tight">{vehicle?.title}</h3>
          <p className="text-white/50 text-xs mt-0.5">{vehicle?.make} {vehicle?.model} · {vehicle?.year} · {vehicle?.seats} seats</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-white/50 mb-3">
          <span className="flex items-center gap-1"><Icon name="Settings" size={11} />{vehicle?.transmission}</span>
          <span className="flex items-center gap-1"><Icon name="Fuel" size={11} />{vehicle?.fuel}</span>
          <span className="flex items-center gap-1 truncate max-w-[100px]"><Icon name="MapPin" size={11} />{vehicle?.location_address?.split(',')?.[0] || 'Nairobi'}</span>
        </div>
        <div className="flex items-end justify-between mt-auto">
          <div>
            <span className="text-[#ffd700] font-black text-xl">KES {pricing?.base_daily_rate?.toLocaleString() || '—'}</span>
            <span className="text-white/40 text-xs">/day</span>
          </div>
          <div className="text-right">
            <div className="text-white/40 text-xs">Deposit</div>
            <div className="text-white/70 text-sm font-semibold">KES {pricing?.deposit_amount?.toLocaleString() || '—'}</div>
          </div>
        </div>
      </div>
      {/* Quick Book Button */}
      <div className="px-4 pb-4">
        <button
          onClick={(e) => { e?.stopPropagation(); onBook(vehicle); }}
          disabled={!isAvailable}
          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 ${
            isAvailable
              ? 'text-[#0a1628] hover:brightness-110 active:scale-[0.98]'
              : 'cursor-not-allowed opacity-50 text-white/50'
          }`}
          style={isAvailable ? { background: 'linear-gradient(135deg, #ffd700 0%, #f0c800 100%)' } : { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
        >
          <Icon name={isAvailable ? 'Zap' : 'Clock'} size={15} />
          {isAvailable ? 'Book Now' : availability?.label}
        </button>
      </div>
    </div>
  );
};

const SkeletonVehicleCard = () => (
  <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 animate-pulse">
    <div className="h-48 bg-white/10" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-white/10 rounded w-3/4" />
      <div className="h-3 bg-white/10 rounded w-1/2" />
      <div className="h-6 bg-white/10 rounded w-1/3" />
    </div>
    <div className="px-4 pb-4">
      <div className="h-10 bg-white/10 rounded-xl" />
    </div>
  </div>
);

const CarHireListingScreen = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    vehicleType: '',
    transmission: '',
    fuel: '',
    rentalMode: '',
    minPrice: '',
    maxPrice: '',
    seats: '',
    search: '',
    availableOnly: false,
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data, error } = await supabase
        ?.from('vehicles')
        ?.select(`*, vehicle_pricing(*), vehicle_media(*), vehicle_availability(*)`)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false });
      if (!error) setVehicles(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles?.filter(v => {
    const pricing = v?.vehicle_pricing?.[0];
    if (filters?.vehicleType && v?.vehicle_type !== filters?.vehicleType) return false;
    if (filters?.transmission && v?.transmission !== filters?.transmission) return false;
    if (filters?.fuel && v?.fuel !== filters?.fuel) return false;
    if (filters?.rentalMode && v?.rental_mode !== filters?.rentalMode && v?.rental_mode !== 'both') return false;
    if (filters?.seats && v?.seats < parseInt(filters?.seats)) return false;
    if (filters?.minPrice && pricing?.base_daily_rate < parseFloat(filters?.minPrice)) return false;
    if (filters?.maxPrice && pricing?.base_daily_rate > parseFloat(filters?.maxPrice)) return false;
    if (filters?.availableOnly && getAvailabilityStatus(v)?.label !== 'Available Now') return false;
    if (filters?.search) {
      const q = filters?.search?.toLowerCase();
      return v?.title?.toLowerCase()?.includes(q) || v?.make?.toLowerCase()?.includes(q) || v?.model?.toLowerCase()?.includes(q);
    }
    return true;
  });

  const handleBook = (vehicle) => {
    navigate(`/car-detail?id=${vehicle?.id}`);
  };

  const availableCount = vehicles?.filter(v => getAvailabilityStatus(v)?.label === 'Available Now')?.length;

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🚗</span>
            <span className="text-white font-black text-lg">Car Hire</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/host-dashboard')} className="text-[#ffd700] text-sm font-bold hover:text-[#f0c800] transition-colors">
              Host Dashboard
            </button>
            <button onClick={() => navigate('/list-my-car')} className="text-[#ffd700] text-sm font-bold hover:text-[#f0c800] transition-colors">
              List Your Car →
            </button>
          </div>
        </div>
      </div>
      {/* Hero */}
      <div className="relative overflow-hidden py-16 px-4" style={{ background: 'linear-gradient(135deg, #0d1f3c 0%, #0a1628 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,215,0,1) 1px, transparent 0)', backgroundSize: '48px 48px' }} />
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-5 border" style={{ background: 'rgba(255,215,0,0.08)', borderColor: 'rgba(255,215,0,0.2)' }}>
            <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
            <span className="text-[#ffd700] text-sm font-semibold">{loading ? '...' : availableCount} vehicles available now</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-black text-white mb-4">
            Drive on Your<br /><span className="text-[#ffd700]">Own Terms</span>
          </h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto mb-8">Browse verified vehicles. Book instantly. Drive with confidence.</p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search by make, model, or title..."
              value={filters?.search}
              onChange={e => setFilters(f => ({ ...f, search: e?.target?.value }))}
              className="w-full pl-12 pr-4 py-4 rounded-2xl text-white placeholder-white/30 text-sm font-medium outline-none focus:ring-2 focus:ring-[#ffd700]/40"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
            />
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { key: 'vehicleType', label: 'Type', options: [{ value: '', label: 'All Types' }, { value: 'sedan', label: 'Sedan' }, { value: 'suv', label: 'SUV' }, { value: 'pickup', label: 'Pickup' }, { value: 'van', label: 'Van' }, { value: 'luxury', label: 'Luxury' }] },
            { key: 'transmission', label: 'Transmission', options: [{ value: '', label: 'Any Transmission' }, { value: 'automatic', label: 'Automatic' }, { value: 'manual', label: 'Manual' }] },
            { key: 'rentalMode', label: 'Mode', options: [{ value: '', label: 'Any Mode' }, { value: 'self_drive', label: 'Self-Drive' }, { value: 'chauffeur', label: 'Chauffeur' }] },
            { key: 'seats', label: 'Seats', options: [{ value: '', label: 'Any Seats' }, { value: '4', label: '4+ Seats' }, { value: '5', label: '5+ Seats' }, { value: '7', label: '7+ Seats' }] },
          ]?.map(filter => (
            <select
              key={filter?.key}
              value={filters?.[filter?.key]}
              onChange={e => setFilters(f => ({ ...f, [filter?.key]: e?.target?.value }))}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-medium outline-none cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              {filter?.options?.map(opt => <option key={opt?.value} value={opt?.value} style={{ background: '#0d1f3c' }}>{opt?.label}</option>)}
            </select>
          ))}
          <div className="flex items-center gap-2">
            <input type="number" placeholder="Min KES" value={filters?.minPrice} onChange={e => setFilters(f => ({ ...f, minPrice: e?.target?.value }))}
              className="w-28 px-3 py-2.5 rounded-xl text-white text-sm outline-none" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }} />
            <span className="text-white/40 text-sm">—</span>
            <input type="number" placeholder="Max KES" value={filters?.maxPrice} onChange={e => setFilters(f => ({ ...f, maxPrice: e?.target?.value }))}
              className="w-28 px-3 py-2.5 rounded-xl text-white text-sm outline-none" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }} />
          </div>
          {/* Available Only Toggle */}
          <button
            onClick={() => setFilters(f => ({ ...f, availableOnly: !f?.availableOnly }))}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${filters?.availableOnly ? 'text-[#0a1628]' : 'text-white/70 hover:text-white'}`}
            style={filters?.availableOnly
              ? { background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: '1px solid rgba(34,197,94,0.4)' }
              : { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }
            }
          >
            <Icon name="CheckCircle" size={15} />
            Available Only
          </button>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-white/50 text-sm">
            {loading ? 'Loading vehicles...' : `${filteredVehicles?.length} vehicle${filteredVehicles?.length !== 1 ? 's' : ''} found`}
          </p>
          {!loading && (
            <div className="flex items-center gap-4 text-xs text-white/40">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#22c55e]" />Available</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#ef4444]" />Booked</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#f97316]" />Unavailable</span>
            </div>
          )}
        </div>

        {/* Vehicle Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6]?.map(i => <SkeletonVehicleCard key={i} />)}
          </div>
        ) : filteredVehicles?.length === 0 ? (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🚗</div>
            <h3 className="text-white font-bold text-xl mb-2">No vehicles found</h3>
            <p className="text-white/40 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles?.map(vehicle => (
              <VehicleCard
                key={vehicle?.id}
                vehicle={vehicle}
                onClick={() => navigate(`/car-detail?id=${vehicle?.id}`)}
                onBook={handleBook}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarHireListingScreen;
